import { useEffect, useRef, useState } from "react";
import HTMLFlipBook from "react-pageflip";
import * as pdfjsLib from "pdfjs-dist/webpack";
import { useParams } from "react-router-dom";
import { FiGrid, FiZoomIn, FiZoomOut, FiMaximize, FiMoreVertical } from "react-icons/fi";
import { FiVolume2, FiVolumeX } from "react-icons/fi";

export default function FlipBookReader({ setIsFullscreen }) {
  const { id } = useParams();
  const [magazine, setMagazine] = useState({});
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [isFullscreenLocal, setIsFullscreenLocal] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [singlePageMode, setSinglePageMode] = useState(false);
  const [centerShift, setCenterShift] = useState(0);
  const [showThumbs, setShowThumbs] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [pageTexts, setPageTexts] = useState([]);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  let minZoom;
  let maxZoom;
 

  if (isMobile && singlePageMode) {
    // 📱 Mobile + Single Page
    minZoom = 2;
    maxZoom = 2.4;
  } else if (isMobile && !singlePageMode) {
    // 📱 Mobile + Double Page
    minZoom = 2.5;
    maxZoom = 3;   // 🔥 you can adjust this
  }
  else {
    // 💻 Desktop (both modes)
    if (singlePageMode) {
      minZoom = 1;
      maxZoom = 1.3;
    }
    else {
      minZoom = 1;
      maxZoom = 1.3;
    }
  }
  useEffect(() => {
    setZoom(minZoom);
  }, [singlePageMode, isMobile]);

  const flipBookRef = useRef(null);
  const isProgrammaticFlip = useRef(false);
  const menuRef = useRef(null);
  const thumbnailRefs = useRef([]);
  const prevPageRef = useRef(0);
  const initialPageRef = useRef(0);

  /* ---------- Effects ---------- */

  useEffect(() => {
    if (!singlePageMode) {
      // when leaving single page mode → re-center book
      setCenterShift(0);
    } else {
      // when entering single page mode → align based on current page
      if (currentPage % 2 !== 0) setCenterShift(150);
      else setCenterShift(-150);
    }
  }, [singlePageMode]);
  useEffect(() => {
    if (!id) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazines/${id}`)
      .then((r) => r.json())
      .then(setMagazine)
      .catch(() => { });
  }, [id]);

  useEffect(() => {
    const handler = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreenLocal(fs);
      setIsFullscreen?.(fs);
    };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, [setIsFullscreen]);
  const useStretch = !isMobile || (isMobile && !singlePageMode);


  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    if (!voiceEnabled) return;
    if (!pages.length) return;

    const timer = setTimeout(() => {
      speakCurrentPage(currentPage);
    }, 200);

    return () => clearTimeout(timer);

  }, [currentPage, voiceEnabled]);

  const iconBase =
    "p-2 rounded-md transition-all duration-200 flex items-center justify-center";

  const iconHover =
    "hover:bg-blue-700 hover:text-blue-400";

  const iconActive =
    "bg-gray-700 text-white shadow-md";

  /* ---------- PDF Loader ---------- */
  const loadPDF = async (url) => {
    const pdf = await pdfjsLib.getDocument(url).promise;
    const imgs = [];
    const texts = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 2 });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({
        canvasContext: canvas.getContext("2d"),
        viewport,
      }).promise;
      imgs.push(canvas.toDataURL());
      const textContent = await page.getTextContent();
      const sortedItems = textContent.items.sort((a, b) => {
        const ay = a.transform[5];
        const by = b.transform[5];

        if (Math.abs(ay - by) > 5) {
          return by - ay; // top → bottom
        }

        return a.transform[4] - b.transform[4]; // left → right
      });

      const pageText = sortedItems.map((item) => item.str).join(" ");

      texts.push(pageText);
      setLoadingProgress(Math.round((i / pdf.numPages) * 100));
    }
    setPages(imgs);
    setPageTexts(texts);
    setCurrentPage(0);
  };
  const speakCurrentPage = (pageIndex) => {
    if (!pageTexts.length) return;

    window.speechSynthesis.cancel();

    let text = "";

    if (singlePageMode) {
      text = pageTexts[pageIndex] || "";
    } else {
      const left = pageTexts[pageIndex] || "";
      const right = pageTexts[pageIndex + 1] || "";
      text = left + " " + right;
    }

    if (!text.trim()) return;

    const speech = new SpeechSynthesisUtterance(text);

    speech.rate = 1;
    speech.pitch = 1;
    speech.lang = "en-US";

    window.speechSynthesis.speak(speech);
  };

  useEffect(() => {
    if (magazine?.pdf_file) {
      loadPDF(`${process.env.REACT_APP_BACKEND_URL}${magazine.pdf_file}`);
    }
  }, [magazine]);
  useEffect(() => {
    if (!pages.length) return;

    if (isMobile) {
      setSinglePageMode(true);
      setZoom(2);
    }
    else if (isMobile && !singlePageMode) {
      setZoom(1);
    }  // 🔥 IMPORTANT
    else {
      setSinglePageMode(false);
      setZoom(1);
    }
    // always reset shift when layout changes
    setCenterShift(0);
  }, [isMobile, pages.length]);
  useEffect(() => {
    const el = thumbnailRefs.current[currentPage];
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentPage]);
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const leftArrowOffset = (() => {
    if (!isFullscreenLocal) {
      if (isMobile) return showThumbs ? 80 : 8;
      return showThumbs ? 153 : 8;
    }

    // fullscreen
    if (isMobile) return showThumbs ? 90 : 16;
    return showThumbs ? 160 : 20;
  })();

  const rightArrowOffset = (() => {
    // 🚫 thumbnails should NOT affect right arrow outside fullscreen
    if (!isFullscreenLocal) {
      return 8;
    }

    // fullscreen only
    if (isMobile) return 16;
    return 20;
  })();




  /* ---------- Navigation ---------- */
  const goToPage = (index) => {
    if (!flipBookRef.current) return;

    isProgrammaticFlip.current = true;

    try {
      flipBookRef.current.pageFlip().turnToPage(index);
      setCurrentPage(index);

      // 🔹 Apply center shift if single page mode
      if (singlePageMode) {
        if (index % 2 === 0) setCenterShift(-150);
        else setCenterShift(150);
      } else {
        setCenterShift(0);
      }

    } catch (err) {
      console.warn("Error turning to page", index, err);
    }

    setTimeout(() => {
      isProgrammaticFlip.current = false;
    }, 600);
  };
  const onFlip = (e) => {
    if (!isProgrammaticFlip.current) {
      const page = e.data;
      setCurrentPage(page);
      const direction = page > prevPageRef.current ? "right" : "left";

      // 🔹 Apply shift only in single page mode
      if (singlePageMode) {

        // IMPORTANT: only shift when NOT flipping pairs
        // i.e., only when switching center inside same spread
        if (page === 0 || page === pages.length - 1) {
          if (isMobile) {
            setCenterShift(-150);
          }
          else {
            setCenterShift(0)
          }
        }
        if (page === 1) {
          setCenterShift(150);
        }
        if (direction === "right") {
          if (page % 2 !== 0) {
            // left page centered
            setCenterShift(150);
          } else {
            // right page centered
            setCenterShift(-150);
          }
        }
        if (direction === "left" && page !== 0) {

          setCurrentPage(page + 1)
          if (page % 2 === 0) {
            // left page centered
            setCenterShift(150);
          } else {
            // right page centered
            setCenterShift(-150);
          }
        }

      } else {
        setCenterShift(0);
      }
      prevPageRef.current = page;
    }
  };
  const handlePrev = () => {
    if (!flipBookRef.current) return;

    const pf = flipBookRef.current.pageFlip();
    if (!pf) return;

    // 🔹 Normal double page mode
    if (!singlePageMode) {
      pf.flipPrev();
      return;
    }

    // 🔹 Single Page Mode (Mobile + Desktop SAME behavior)

    if (currentPage === 0) return; // prevent negative

    if (currentPage % 2 === 1) {
      // Currently on right page → move to left page
      pf.flipPrev();
    } else {
      // Currently on left page → jump to previous right page
      const newPage = currentPage - 1;
      pf.turnToPage(newPage);
      setCurrentPage(newPage);

      // Apply shift manually
      setCenterShift(150);
    }
  };
  const handleNext = () => {
    if (!flipBookRef.current) return;

    const pf = flipBookRef.current.pageFlip();
    if (!pf) return;

    // 🔹 Normal double page mode
    if (!singlePageMode) {
      pf.flipNext();
      return;
    }

    // 🔹 Single Page Mode (Mobile + Desktop SAME behavior)

    if (currentPage >= pages.length - 1) return;

    if (currentPage % 2 === 0) {
      // Currently on left page → move to right page
      pf.flipNext();
    } else {
      // Currently on right page → jump to next left page
      const newPage = currentPage + 1;
      pf.turnToPage(newPage);
      setCurrentPage(newPage);

      // Apply shift manually
      setCenterShift(-150);
    }
  };
  /* ---------- Loader ---------- */
  if (!pages.length) {
    return (
      <div className="w-full h-[600px] flex flex-col items-center justify-center">
        <div className="relative w-14 h-14">
          {/* Gray static ring */}
          <div className="absolute inset-0 rounded-full border-4 border-gray-300" />

          {/* Blue rotating arc */}
          {/* <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent border-r-transparent animate-spin" /> */}
          <div className="absolute inset-0 rounded-full border-4 border-blue-600
                border-t-transparent border-r-transparent border-b-transparent
                animate-spin" />

        </div>

        <div className="mt-4 bg-white px-5 py-2 rounded font-semibold">
          Loading PDF… {loadingProgress}%
        </div>
      </div>

    );
  }

  /* ---------- UI ---------- */
  return (
    // <div className="relative w-screen min-h-[120h] l-min-h-[120vh] bg-gray-400 flex flex-col items-center overflow-hidden">
    <div
      className={`
    relative
    sm:w-full
    w-[412px]
    bg-gray-400
    flex
    flex-col
    items-center
    justify-center
    gap-5
    overflow-hidden
    // h-[360px]      /* default (mobile <640px) */
    ${isFullscreenLocal ? "h-[950px]" : isMobile ? "h-[500px]" : "h-[360px]"}
    sm:min-h-[50vh]     /* ≥640px */
    md:min-h-[60vh]     /* ≥768px */
    lg:min-h-[80vh]     /* ≥1024px */
    xl:min-h-[125vh]    /* ≥1280px */
    px-2 md:px-4 lg:px-6
    `
      }
    >
      {/* LEFT ARROW */}
      <button
        onClick={handlePrev}
        className={`
    top-1/2
    ${currentPage === 0 ? "hidden" : "flex"}
    ${isFullscreenLocal ? "fixed" : "absolute"}
    -translate-y-1/2
    z-[9999999]

    w-14 h-14
    rounded-full
    bg-gray-500/40 hover:bg-gray-500/60
    backdrop-blur-sm

    items-center justify-center
    text-white text-4xl
    cursor-pointer
  `}
        style={{
          left: leftArrowOffset,
          WebkitTapHighlightColor: "transparent",
          transform: "translateY(-50%)", // 🔒 lock transform
        }}
      >
        &lt;
      </button>


      {/* RIGHT ARROW */}
      <button
        onClick={handleNext}
        className={`
    top-1/2
    ${currentPage === pages.length - 1 ? "hidden" : "flex"}
    ${isFullscreenLocal ? "fixed" : "absolute"}
    -translate-y-1/2
    z-[9999999]

    w-14 h-14
    rounded-full
    bg-gray-500/40 hover:bg-gray-500/60
    backdrop-blur-sm

    items-center justify-center
    text-white text-4xl
    cursor-pointer
  `}
        style={{
          right: rightArrowOffset,
          WebkitTapHighlightColor: "transparent",
          transform: "translateY(-50%)", // 🔒 lock transform
        }}
      >
        &gt;
      </button>


      {/* Flipbook */}
      <div
        className={` sm:w-full flex justify-center ${isFullscreenLocal ? "fixed inset-0 z-[9999]" : ""
          }`}
      >
        <div
          className={`
    relative
    ${isMobile && singlePageMode ? "w-auto" : "w-full"}
    sm:max-w-[90vw]
    md:max-w-[85vw]
    lg:max-w-[80vw]
    xl:max-w-[70vw]
    flex
    ${isMobile ? "justify-center" : "justify-start"}
    transition-transform
    items-center   // 🔥 THIS IS IMPORTANT
    sm:pb-24
    pb-4
  `}
          style={{
            transform: `
  translateX(${centerShift}px)
  scale(${zoom})
`,
            top: isFullscreenLocal && window.innerWidth < 1024 ? "30px" : "0",
            position: isFullscreenLocal ? "relative" : "static",

          }}

        >
          <HTMLFlipBook
            key={`${isMobile}-${singlePageMode}`}
            ref={flipBookRef}
            startPage={initialPageRef.current}
            /* 🔥 WIDTH CONTROL */
            width={
              isMobile
                ? singlePageMode
                  ? 160  //8 🔥 smaller base width
                  : 80
                : singlePageMode
                  ? 300
                  : 475
            }

            height={
              isMobile
                ? singlePageMode
                  ? 285 // 🔥 smaller base height
                  : 120
                : 900
            }
            minWidth={150}
            maxWidth={800}
            minHeight={200}
            maxHeight={1000}
            usePortrait={false}
            size={isMobile ? "fixed" : "stretch"}
            mobileScrollSupport={false}
            showCover
            onFlip={onFlip}
          >


            {pages.map((src, i) => (
              <div key={i} className="flex items-center justify-center">
                <img src={src} alt="" className="w-full h-full object-contain" />
              </div>
            ))}
          </HTMLFlipBook>
        </div>
      </div>

      {/* Bottom Bar */}
      {/* <div
        className={`${isFullscreenLocal ? "fixed" : "absolute"
          } bottom-[58px] left-1/2 -translate-x-1/2 max-w-[320px] w-full bg-gray-500 px-[7px] py-[7px] rounded-[10px] flex justify-between items-center z-[999999999]`}
      > */}
      <div
        className={`
    ${isFullscreenLocal ? "fixed" : "absolute"}
    bottom-2 sm:bottom-4
    left-1/2 -translate-x-1/2
    w-[74%] sm:w-[90%] md:w-[70%] lg:w-[50%] xl:w-[40%]
    max-w-[400px]
    bg-gray-500
    px-1 sm:px-2
    sm:py-2
    sm:mt-6
    rounded-xl
    flex justify-between items-center
    z-[999999999]
  `}
      >

        <div className="text-sm font-semibold text-white">{currentPage + 1}/{pages.length}</div>

        <div className="flex gap-0 items-center">
          {/* Zoom In */}
          <button className={` bg-transparent`} onClick={() => setZoom((z) => Math.min(z + 0.1, maxZoom))}>
            <FiZoomIn className="w-4 h-4 sm:w-5 sm:h-5" />

          </button>

          {/* Zoom Out */}
          <button disabled={zoom <= minZoom} className={`${iconBase} ${iconHover} bg-transparent`} onClick={() => setZoom((z) => Math.max(z - 0.1, minZoom))}>
            <FiZoomOut className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          {/* Fullscreen */}
          <button
            className={`${iconBase} ${iconHover} bg-transparent`}
            onClick={() => {
              if (!document.fullscreenElement) {
                document.documentElement.requestFullscreen();
              } else {
                document.exitFullscreen();
              }
            }}
          >
            <FiMaximize className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>


          {/* Thumbnails */}
          <button
            className="bg-transparent"
            onClick={() => setShowThumbs((s) => !s)}
          >
            <FiGrid className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <button
            className="bg-transparent"
            onClick={() => {
              if (voiceEnabled) {
                window.speechSynthesis.pause();
                setVoiceEnabled(false);
              } else {
                setVoiceEnabled(true);

                if (window.speechSynthesis.paused) {
                  window.speechSynthesis.resume();
                } else {
                  speakCurrentPage(currentPage);
                }
              }
            }}
          >
            {voiceEnabled ? (
              <FiVolume2 className="w-4 h-4 sm:w-5 sm:h-5" />
            ) : (
              <FiVolumeX className="w-4 h-4 sm:w-5 sm:h-5" />
            )}
          </button>

          {/* Menu */}
          <div className="relative" ref={menuRef}>
            <button
              className="bg-transparent"
              onClick={() => setMenuOpen((m) => !m)}
            >

              <FiMoreVertical className="w-4 h-4 sm:w-5 sm:h-5" />

            </button>

            {menuOpen && (
              <div className="absolute bottom-full mb-2 right-0 w-44 bg-white border border-gray-300 rounded shadow-md z-[200]">
                <button
                  className="w-full px-4 py-2 text-left bg-white text-gray-500 focus:outline-none focus:bg-transparent hover:bg-gray-100"
                  onClick={() => {
                    initialPageRef.current = currentPage;
                    setSinglePageMode((s) => !s);
                    setMenuOpen(false);
                  }}
                >
                  {singlePageMode ? "Double Page Mode" : "Single Page Mode"}
                </button>
                <button
                  className="w-full px-4 py-2 text-left bg-white text-gray-500 focus:outline-none focus:bg-transparent hover:bg-gray-100"
                  onClick={() => {
                    goToPage(0);
                    setMenuOpen(false);
                  }}
                >
                  Go To First Page
                </button>
                <button
                  className="w-full px-4 py-2 text-left bg-white text-gray-500 focus:outline-none focus:bg-transparent hover:bg-gray-100"
                  onClick={() => {
                    goToPage(pages.length - 1);
                    setMenuOpen(false);
                  }}
                >
                  Go To Last Page
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* LEFT THUMBNAILS — LEFT SIDE PANEL */}
      {showThumbs && (
        <div
          className={`
          ${isFullscreenLocal ? "fixed" : "absolute"}
          top-1/2 left-0 -translate-y-1/2
          z-[10000]
          bg-black/50 p-2 rounded-md
          shadow-lg flex flex-col items-center
          h-[80%] w-36
        `}
        >

          {/* Header */}
          <div className="w-full flex justify-between items-center mb-2 px-1">
            <span className="text-xs font-bold text-white tracking-wide">THUMBNAILS</span>
            <button
              onClick={() => setShowThumbs(false)}
              className="text-white text-xs px-1 py-0.5 rounded hover:bg-white/20"
            >
              ✕
            </button>
          </div>

          {/* Thumbnails List */}
          <div className="flex flex-col gap-2 overflow-y-auto w-full flex-1">
            {pages.map((src, i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                ref={(el) => (thumbnailRefs.current[i] = el)}
                className={`
                flex flex-col items-center w-full p-1 rounded
               border-2 
                ${i === currentPage ? "border-yellow-400" : "border-transparent"}
                hover:border-yellow-300
                focus:border-transparent
                active:border-transparent


                outline-none
                focus:outline-none
                focus-visible:outline-none
                focus-visible:ring-0
                active:bg-transparent
                bg-transparent
              `}
                style={{ WebkitTapHighlightColor: "transparent" }}
              >

                <img
                  src={src}
                  alt={`Page ${i + 1}`}
                  className="w-20 h-24 object-cover rounded-sm"
                />
                <span className="text-[10px] text-white mt-1">{i + 1}</span>
              </button>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}